/** @jsxImportSource @emotion/react */
import React, { memo, useContext } from 'react'
import isArray from 'lodash/isArray'
import isObject from 'lodash/isObject'
import { isNonCommonUiTest } from '@signal/utils/env/isTest'
import renderIf from '@signal/utils/renderIf'
import AllowCommonUiInTestContext from './Context'

// avoid giant storyshots
const MAXIMUM_DOM_ARRAY_OBJECT_SIZE = 5000

/**
 * Any common code component should be wrapped with this HOC to avoid the snapshots from apps which import this
 * control from being unit tested
 * @param {Object} Component - the component to wrap witht his HOC
 * @return {Object} - the wrapped verison of the component
 */
function withCommonSnapshotsOnly<T>(Component: T): T {
  if (!isNonCommonUiTest()) {
    return Component
  }

  // this component will be returned instead, to go into the storyshots of applications using common code
  const EmptyComponent: React.FC<{ children?: React.ReactNode }> = ({
    children,
    ...rest
  }) => {
    const allow = useContext(AllowCommonUiInTestContext)

    // we want to blit properties to the DOM, to help with unit testing (of the particular usage the app
    // is making of a common component)
    let divRest = rest as Record<string, unknown>
    if (!allow) {
      divRest = Object.keys(divRest).reduce((newRest, key) => {
        const val = divRest[key]
        try {
          if (isObject(val) || isArray(val)) {
            const entityType = isObject(val) ? 'Object' : 'Array'
            const json = JSON.stringify(val).toLowerCase()
            if (json.length > MAXIMUM_DOM_ARRAY_OBJECT_SIZE) {
              newRest[key] = `[[Large ${entityType}]]`
            }
          }
        } catch (e) {
          // in case of circular dependencies found in some react components
        }
        return newRest
      }, divRest)
    }

    return renderIf(
      allow,
      // @ts-ignore
      () => <Component children={children} {...rest} />,
      () => <div {...divRest}>{children}</div>
    ) as React.ReactElement
  }
  return (memo(EmptyComponent) as unknown) as T
}

export default withCommonSnapshotsOnly
